'use client';

import clsx from 'clsx';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { IconButton } from '../IconButton';
import styles from './Tabs.module.scss';

export interface TabsSectionProps {
  label: string;
  count?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

interface TabsProps {
  id: string;
  tabs: TabsSectionProps[];
  variant?: 'default' | 'responsive';
  overflowPadding?: string;
  testId?: string;
  onTabChange?: (index: number) => void;
}

export const Tabs = ({
  id,
  tabs,
  variant = 'default',
  overflowPadding,
  testId,
  onTabChange,
}: TabsProps) => {
  const t = useTranslations('components.tabs');
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const tabListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    tabRefs.current = tabRefs.current.slice(0, tabs.length);
  }, [tabs]);

  const checkScroll = () => {
    const element = tabListRef.current;
    if (!element) return;

    const scrollLeft = element.scrollLeft;
    const scrollWidth = element.scrollWidth;
    const clientWidth = element.clientWidth;

    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  useEffect(() => {
    checkScroll();

    const element = tabListRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(element);

    element.addEventListener('scroll', checkScroll);

    return () => {
      resizeObserver.disconnect();
      element.removeEventListener('scroll', checkScroll);
    };
  }, [tabs]);

  const scrollTabs = (direction: 'left' | 'right') => {
    const element = tabListRef.current;
    if (!element) return;

    const scrollAmount = element.clientWidth * 0.8;
    const newScrollLeft =
      direction === 'left' ? element.scrollLeft - scrollAmount : element.scrollLeft + scrollAmount;

    element.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    });
  };

  const handleTabClick = (index: number) => {
    setActiveTabIndex(index);
    onTabChange?.(index);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
    const tabCount = tabs.length;
    let newIndex = activeTabIndex;

    switch (event.key) {
      case 'ArrowRight':
        newIndex = (currentIndex + 1) % tabCount;
        break;
      case 'ArrowLeft':
        newIndex = (currentIndex - 1 + tabCount) % tabCount;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = tabCount - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    setActiveTabIndex(newIndex);
    onTabChange?.(newIndex);

    tabRefs.current[newIndex]?.focus();
  };

  return (
    <div
      className={clsx(styles.wrapper, {
        [styles.wrapperResponsive]: variant === 'responsive',
      })}
      style={{ '--tabs-overflow-padding': overflowPadding } as React.CSSProperties}
      data-testid={testId}
    >
      <div className={styles.tabs}>
        <div className={styles.tabListWrapper}>
          <div role="tablist" className={styles.tabList} ref={tabListRef}>
            {tabs.map((tab, index) => (
              <button
                key={`${id}-tab-${index}`}
                role="tab"
                id={`${id}-tab-${index}`}
                className={clsx(styles.tab, {
                  [styles.tabActive]: activeTabIndex === index,
                })}
                aria-selected={activeTabIndex === index ? 'true' : 'false'}
                aria-controls={`${id}-tabpanel-${index}`}
                tabIndex={activeTabIndex === index ? undefined : -1}
                onClick={() => handleTabClick(index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                ref={(el) => {
                  tabRefs.current[index] = el;
                }}
              >
                <span className={styles.label}>
                  {tab.icon}
                  <span className={styles.labelText}>
                    {tab.label}
                    {tab.count && (
                      <span className={styles.labelCount} aria-label={`Antal: ${tab.count}`}>
                        {tab.count}
                      </span>
                    )}
                  </span>
                </span>
                <span
                  className={clsx(styles.accordionIcon, {
                    [styles.accordionIconActive]: activeTabIndex === index,
                  })}
                  aria-hidden="true"
                >
                  <ChevronDown role="presentation" />
                </span>
              </button>
            ))}
          </div>
          {canScrollLeft && (
            <span className={styles.tabListScrollButtonLeft} aria-hidden="true">
              <IconButton
                size="sm"
                variant="secondary"
                label={t('scrollLeft')}
                onClick={() => scrollTabs('left')}
              >
                <ChevronLeft aria-hidden="true" />
              </IconButton>
            </span>
          )}
          {canScrollRight && (
            <span className={styles.tabListScrollButtonRight} aria-hidden="true">
              <IconButton
                size="sm"
                variant="secondary"
                label={t('scrollRight')}
                onClick={() => scrollTabs('right')}
              >
                <ChevronRight aria-hidden="true" />
              </IconButton>
            </span>
          )}
        </div>
        {tabs.map((tab, index) => (
          <div
            key={`${id}-tabpanel-${index}`}
            role="tabpanel"
            id={`${id}-tabpanel-${index}`}
            className={clsx(styles.tabPanel, `tabPanelOrder--${index}`, {
              [styles.tabPanelActive]: activeTabIndex === index,
            })}
            aria-labelledby={`${id}-tab-${index}`}
          >
            {activeTabIndex === index ? tab.children : null}
          </div>
        ))}
      </div>
    </div>
  );
};
